/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useRef, useState} from 'react';

import InformativeText from '../../constants/InformativeText';
import {SampleExcel} from '../../constants/SampleExcel';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import downstreamLeasedAPI from '../../services/modules/AM/downAssestManagementAPI';
import listingDownstreamLeasedAPI from '../../services/modules/AM/listingDownstreamLeasedAsset';
import vehicleFuelUsedJSON from '../../services/modules/JSON/vehicleFuelUsed';
import vehicleType from '../../services/modules/JSON/vehicleType';
import getTotalUserCount from '../../services/modules/User/totalUserCount';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {
  labelFromOtherStored,
  OTHER_VALUE_PREFIX
} from '../CustomInputs/Select/SelectWithOtherOption';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {
  downPropertyLabel,
  downPropertyKey
} from '../Forms/Constants/IP/dSPropertyKeyLabel';
import DownVehicleForm from '../Forms/DownVehicleForm';
import {emissionUnits} from '../Forms/interface/unitChange';
import changeUnit from '../Forms/Utils/changeUnit';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {createBulkRowSelection} from './Utils/TableBulkSelectionBar';
import TableTitleHeader from './Utils/TableTitleHeader';

const downstreamVehicleTable: React.FC<{
  selectedYear: string;
  isConfigurator?: boolean;
  isAssetManager?: boolean;
}> = ({selectedYear, isConfigurator, isAssetManager = false}) => {
  interface DataType {
    _id: string;
    name: string;
    mobile: string;
    email: string;
    vehicleType: number | string;
    address: string;
    country: number;
    state: number;
    city: number;
    pincode: number;
    fuelUsed: number;
    fuelQuantity: number;
    rentedFrom: string;
    rentedTo: string;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const companyId = useCompanyStore.getState().companyId;
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const vehicleTypes = useJSONStore.getState().vehicleType;
  const vehicleFuelUsed = useJSONStore.getState().vehicleFuelUsed;
  const [showModelInfo, setShowModelInfo] = useState(false);

  const type = 'Vehicle';

  useEffect(() => {
    if (selectedYear) {
      setIsRefresh(true);
      listingDownstreamLeasedAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}/${type}`,
        isAssetManager ? 'dataCollection' : 'valueChain'
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear]);

  const downVehicleKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: downPropertyKey.ASSET_NAME,
      label: downPropertyLabel.ASSET_NAME,
      type: objTypes.string
    },
    // {
    //   key: downPropertyKey.FIRST_NAME,
    //   label: downPropertyLabel.FIRST_NAME,
    //   type: objTypes.string
    // },
    // {
    //   key: downPropertyKey.LAST_NAME,
    //   label: downPropertyLabel.LAST_NAME,
    //   type: objTypes.string
    // },
    // ...(!isAssetManager
    //   ? [
    //       {
    //         key: downPropertyKey.ASSET_MOBILE,
    //         label: downPropertyLabel.ASSET_MOBILE,
    //         type: objTypes.string
    //       },
    //       {
    //         key: downPropertyKey.ASSET_EMAIL,
    //         label: downPropertyLabel.ASSET_EMAIL,
    //         type: objTypes.string
    //       }
    //     ]
    //   : []),
    {
      key: downPropertyKey.VEHICLE_TYPE,
      label: downPropertyLabel.VEHICLE_TYPE,
      type: objTypes.json_has_other_option,
      format: vehicleTypes
    },
    {
      key: downPropertyKey.FUEL_USED,
      label: downPropertyLabel.FUEL_USED,
      type: objTypes.json,
      format: vehicleFuelUsed
    },
    {
      key: downPropertyKey.FUEL_QUANTITY,
      label: downPropertyLabel.FUEL_QUANTITY,
      type: objTypes.relatedUnit,
      relatedKey: downPropertyKey.FUEL_USED,
      format: vehicleFuelUsed
    },
    {
      key: downPropertyKey.RENTED_FROM,
      label: downPropertyLabel.RENTED_FROM,
      type: objTypes.date,
      format: 'DD-MM-YYYY'
    },
    {
      key: downPropertyKey.RENTED_TO,
      label: downPropertyLabel.RENTED_TO,
      type: objTypes.date,
      format: 'DD-MM-YYYY'
    },

    ...(hasUserRole(UserRole.Checker_Role) ||
    (hasUserRole(UserRole.Both_role) && !isConfigurator)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([vehicleType(), vehicleFuelUsedJSON(), getTotalUserCount()])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      Promise.all([
        listingDownstreamLeasedAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}/${type}`,
          isAssetManager ? 'dataCollection' : 'valueChain'
        ),
        getTotalUserCount()
      ])
        .then(([res]) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    downstreamLeasedAPI(APITypeEnum.DELETE_ALL, {
      downstreamleasedasset: _id,
      year: selectedYear
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = [
    {
      title: downPropertyLabel.ASSET_NAME,
      dataIndex: downPropertyKey.ASSET_NAME,
      ...SearchFilter(
        downPropertyLabel.ASSET_NAME,
        downPropertyKey.ASSET_NAME as keyof DataType
      )
    },
    // ...(!isAssetManager
    //   ? [
    //       {
    //         title: downPropertyLabel.ASSET_MOBILE,
    //         dataIndex: downPropertyKey.ASSET_MOBILE,
    //         editable: true,
    //         // width: '200px',
    //         type: 'number'
    //       },
    //       {
    //         title: downPropertyLabel.ASSET_EMAIL,
    //         dataIndex: downPropertyKey.ASSET_EMAIL,
    //         editable: true,
    //         // ...sorter(downPropertyKey.ASSET_EMAIL as keyof DataType),
    //         // width: '200px',
    //         ...SearchFilter(
    //           downPropertyLabel.ASSET_EMAIL,
    //           downPropertyKey.ASSET_EMAIL as keyof DataType
    //         )
    //       }
    //     ]
    //   : []),
    {
      title: downPropertyLabel.VEHICLE_TYPE,
      dataIndex: downPropertyKey.VEHICLE_TYPE,
      editable: true,
      render: (value) => {
        const isNumber = typeof value === 'number';

        return (
          <>
            {!isNumber
              ? value?.startsWith(OTHER_VALUE_PREFIX)
                ? labelFromOtherStored(value)
                : value ?? '-'
              : vehicleTypes.find((item) => {
                  return item.value === value;
                })?.label ?? '-'}
          </>
        );
      }
      // width: '200px',
    },
    {
      title: downPropertyLabel.FUEL_USED,
      dataIndex: downPropertyKey.FUEL_USED,
      editable: true,
      render: (value) => {
        return (
          <>
            {
              vehicleFuelUsed.find((item) => {
                return item.value === value;
              })?.label
            }
          </>
        );
      }
      // width: '200px',
    },
    {
      title: downPropertyLabel.FUEL_QUANTITY,
      dataIndex: downPropertyKey.FUEL_QUANTITY,
      type: 'number',
      editable: true,
      render: (value, record) => {
        const fuelUsedValue =
          record[downPropertyKey.FUEL_USED as keyof DataType];
        const fuelState = vehicleFuelUsed.find((item: any) => {
          return item?.value === fuelUsedValue;
        })?.state;
        const fuelUnit = changeUnit(fuelState);

        return `${formatIndian(value)} ${value ? fuelUnit : ''}`;
      }
    },
    {
      title: downPropertyLabel.RENTED_FROM,
      dataIndex: downPropertyKey.RENTED_FROM,
      editable: true,
      render: (value) => moment(value).format('DD-MM-YYYY')
    },
    {
      title: downPropertyLabel.RENTED_TO,
      dataIndex: downPropertyKey.RENTED_TO,
      editable: true,
      render: (value) => moment(value).format('DD-MM-YYYY')
    },
    ...(isAssetManager
      ? [
          {
            title: downPropertyLabel.TOTAL_EMISSION,
            dataIndex: downPropertyKey.TOTAL_EMISSION,
            editable: true,
            // ...sorter(upstreamKey.TOTAL_FUEL_EMISSIONS as keyof DataType),
            //   width: '100px',
            // filterer: monthlyYearly,            //   return {text: item.label, value: item.value};
            // }),
            // onFilter: (value, record) => {
            //   return record.totalEmission === value;
            // },
            render: (value: any) => {
              return `${formatIndian(value)} ${emissionUnits.T_CO2e}`;
            }
          }
        ]
      : []),
    ...((hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)) &&
    !isConfigurator &&
    !isAssetManager
      ? [
          {
            title: makeCheckKeyLabel.STATUS_COMMENTS.label,
            dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <div
                  style={{
                    fontWeight: 700,
                    color: colorText.color,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}{' '}
                  {value === 'rejected' && record?.comment && (
                    <span className="customComment"> - {record.comment}</span>
                  )}
                </div>
              );
            }
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Checker_Role) ||
    (hasUserRole(UserRole.Both_role) && !isConfigurator)
      ? [
          {
            title: makeCheckKeyLabel.STATUS.label,
            // dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(statusEm.ACCEPTED);

              return (
                <Tag
                  onClick={() => {
                    // setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  color={colorText.color}
                  style={{
                    fontWeight: 700
                  }}
                >
                  {colorText.text}
                </Tag>
              );
            }
          }
        ]
      : []),
    // ...(hasUserRole(UserRole.Checker_Role) ||
    // (hasUserRole(UserRole.Both_role) && !isConfigurator)
    //   ? [
    //       {
    //         title: FullInfo.title,
    //         dataIndex: 'FullInfo',
    //         editable: true,
    //         type: 'select',
    //         render: (_: any, record: any) => {
    //           return (
    //             <Button
    //               onClick={() => {
    //                 setShowModelInfo(true);
    //                 setEditData(record);
    //                 setIsEdit(true);
    //               }}
    //               shape="circle"
    //               style={{
    //                 marginRight: 16,
    //                 cursor: 'pointer',
    //                 color: '#435B37',
    //                 background: '#F5FBF3',
    //                 border: 0
    //               }}
    //             >
    //               <ExclamationOutlined />
    //             </Button>
    //           );
    //         }
    //       }
    //     ]
    //   : []),
    ...(isConfigurator
      ? [
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '' as const,
            fixed: 'right' as const,
            width: '200px',
            render: (value: any, record: any) => {
              return dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                  isConfigurator={isConfigurator}
                />
              ) : null;
            }
          }
        ]
      : [])
  ];

  return (
    <div>
      <TableTitleHeader
        title="Downstream Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Vehicle'
        }}
        sampleDownload={{href: SampleExcel.AssetDownVehicle}}
        jobs={{
          setShowJobs,
          uploadType: uploadTypes.AssetDownVehicle
        }}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        onlyYear={true}
        showAddOnly={!isConfigurator}
        onlyUpload={!isConfigurator}
        tooltip={InformativeText.AM.downLeased}
        shouldEdit={
          isConfigurator
            ? !hasUserRole(UserRole.View_Only)
            : hasUserRole(UserRole.Maker_Role) ||
              hasUserRole(UserRole.Both_role)
        }
        onSelectAll={
          isConfigurator &&
          (hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role))
            ? handleSelectAll
            : undefined
        }
        allSelectableCount={dataSource.length}
        onDeselectAll={() => {
          setSelectedRowKeys([]);
        }}
        shouldDisable={refresh}
        // accessModuleType={accessModule.externalStakeholders}
        // accessModuleDataLimit={getAvailableDataLimit(
        //   countModule.externalStakeholders
        // )}
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={createBulkRowSelection<DataType>({
          selectedRowKeys,
          setSelectedRowKeys,
          onSelectAll: handleSelectAll,
          onDeselectAll: () => {
            setSelectedRowKeys([]);
          },
          enabled:
            isConfigurator &&
            (hasUserRole(UserRole.Maker_Role) ||
              hasUserRole(UserRole.Both_role))
        })}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />

      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
        type={uploadTypes.AssetDownVehicle}
        details={`${null}/${selectedYear}`}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={<DownVehicleForm />}
        API={downstreamLeasedAPI}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        type={{key: 'type', value: type}}
        onlyYear={true}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        stepsTitle={['Vehicle Details']}
        formPreview={downVehicleKeyLabel}
      />

      {/* <DisplayModal
        title={openDisplayModalType.title}
        open={openDisplayModal}
        onCancel={() => {
          setOpenDisplayModal(false);
        }}
        displayComponent={
          <TimeFrameDataTooltip mainData={openDisplayModalData} />
        }
      /> */}

      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={downVehicleKeyLabel} />
        }
        API={downstreamLeasedAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        formPreview={downVehicleKeyLabel}
        isConfigurator={isConfigurator}
      />
    </div>
  );
};

export default downstreamVehicleTable;
